/**
 * Comparison strategies for a column's cell values, keyed by the name a column puts in `compare`.
 *
 * Each takes two values already extracted by `sortValueFor` and returns the usual negative, zero or
 * positive. Nullish values never reach one — `withNullsFirst` settles those before delegating.
 *
 *   - `auto`:    numbers numerically, everything else by locale
 *   - `text`:    locale comparison of the string form
 *   - `numeric`: numeric comparison
 *   - `date`:    chronological, parsing anything `Date` accepts
 *   - `boolean`: false before true
 */
export const comparisonStrategies = Object.freeze({
  auto: compareAuto,
  text: (left, right) => String(left).localeCompare(String(right)),
  numeric: (left, right) => Number(left) - Number(right),
  date: (left, right) => toTime(left) - toTime(right),
  boolean: (left, right) => Number(Boolean(left)) - Number(Boolean(right)),
});

/**
 * Returns the value a column sorts on, which `sortValue` may compute from the whole row: a column
 * can show "2 days ago" and sort on the timestamp behind it.
 *
 * @param {Object} column
 * @param {Object} row
 * @returns {*}
 */
export function sortValueFor(column, row) {
  return typeof column.sortValue === "function" ? column.sortValue(row) : row?.[column.field];
}

/**
 * Returns the comparison strategy a column asks for. `compare` is either the name of a built-in
 * strategy or a function `(leftValue, rightValue, leftRow, rightRow)`; an unknown name falls back to
 * `auto`.
 *
 * @param {Object} column
 * @returns {Function}
 */
export function resolveComparison(column) {
  if (typeof column.compare === "function") {
    return column.compare;
  }

  return comparisonStrategies[column.compare] ?? comparisonStrategies.auto;
}

/**
 * Returns a row comparator for one column and direction, ready to hand to a sort algorithm.
 *
 * @param {Object} column
 * @param {String} direction - "asc" or "desc"
 * @returns {Function} (leftRow, rightRow) => number
 */
export function createRowComparator(column, direction) {
  const compare = withNullsFirst(resolveComparison(column));
  const sign = direction === "asc" ? 1 : -1;

  return function compareRows(leftRow, rightRow) {
    const left = sortValueFor(column, leftRow);
    const right = sortValueFor(column, rightRow);

    return sign * compare(left, right, leftRow, rightRow);
  };
}

// Nullish values order ahead of present ones, so a strategy only ever sees values it can compare.
function withNullsFirst(compare) {
  return function compareWithNullsFirst(left, right, leftRow, rightRow) {
    if (left == null || right == null) {
      if (left == null && right == null) {
        return 0;
      }

      return left == null ? -1 : 1;
    }

    return compare(left, right, leftRow, rightRow);
  };
}

function compareAuto(left, right) {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left).localeCompare(String(right));
}

function toTime(value) {
  const time = value instanceof Date ? value.getTime() : Date.parse(value);

  return Number.isNaN(time) ? 0 : time;
}
